"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { homepageService, type TRailMatch } from "../services";

/** The history-state field the open drawer leaves behind. */
const STATE_KEY = "railDrawer";
/** Next rewrites an entry's state when the page comes back to it, keeping only its own
 *  fields; its `key` survives, so the slug is also kept in session storage under it. */
const storageKey = (entryKey: unknown) =>
  `travela_rail_drawer:${String(entryKey)}`;

type THistoryState = Record<string, unknown> | null | undefined;

/** The current entry's URL: pops to other URLs are other pages, Next's to handle. */
const pageUrl = () => window.location.pathname + window.location.search;

const remember = (entryKey: string, slug: string) => {
  try {
    sessionStorage.setItem(storageKey(entryKey), slug);
  } catch {
    // Storage off: back from a listing just won't reopen the drawer.
  }
};

const openSlug = (state: THistoryState): string | null => {
  const slug = state?.[STATE_KEY];
  if (typeof slug === "string" && slug) return slug;
  if (!state?.key) return null;
  try {
    return sessionStorage.getItem(storageKey(state.key));
  } catch {
    return null;
  }
};

/**
 * A rail's page as the app's pushed screen, on mobile: a drawer instead of a new URL.
 * Opening pushes a history entry on the same URL, so the phone's back closes it (and
 * forward reopens it); coming back from a listing opened inside it reopens it too.
 */
export const useRailDrawer = (resolve: (slug: string) => TRailMatch | null) => {
  const [match, setMatch] = useState<TRailMatch | null>(null);
  const resolveRef = useRef(resolve);
  resolveRef.current = resolve;
  // The page the drawer belongs to, by URL: pops to other pages are Next's.
  const pageAs = useRef<string | null>(null);

  // Back from a listing opened in the drawer: reopen it once the rails can resolve it.
  const pending = useRef<string | null>(null);
  useEffect(() => {
    pageAs.current = pageUrl();
    pending.current = openSlug(window.history.state as THistoryState);
  }, []);
  useEffect(() => {
    if (!pending.current) return;
    const found = resolve(pending.current);
    if (!found) return;
    pending.current = null;
    setMatch(found);
  }, [resolve]);

  // The phone's back (or forward) onto one of this page's entries opens or closes it.
  useEffect(() => {
    const onPop = (event: PopStateEvent) => {
      const popped = event.state as THistoryState;
      if (!popped || pageUrl() !== pageAs.current) return;
      const slug = openSlug(popped);
      setMatch(slug ? resolveRef.current(slug) : null);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const open = useCallback((next: TRailMatch) => {
    const state = (window.history.state ?? {}) as Record<string, unknown>;
    pageAs.current = pageUrl();
    const slug = homepageService.matchDetails(next).slug;
    // An entry of its own (Next's scroll memory is per key, too).
    const key = `${String(state.key ?? "page")}~${slug}`;
    remember(key, slug);
    window.history.pushState({ ...state, key, [STATE_KEY]: slug }, "");
    setMatch(next);
  }, []);

  const close = useCallback(() => {
    if (openSlug(window.history.state as THistoryState)) window.history.back();
    else setMatch(null);
  }, []);

  return { match, open, close };
};
